import React, { useState, useEffect, useCallback } from 'react';
import { Users, ArrowRight, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoPromoteTab({ auth, dealers, loadDealers }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoteModal, setPromoteModal] = useState(null); // { installer_company, count, warranties }
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [form, setForm] = useState({
    dealer_id: '',
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    username: '',
    password: '',
    pin_code: '',
  });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/demo-registrations`, { headers: { 'Authorization': `Basic ${auth}` } });
      if (res.ok) setGroups(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [auth]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (loadDealers && (!dealers || dealers.length === 0)) loadDealers(); }, [dealers, loadDealers]);

  const openPromote = (g) => {
    setPromoteModal(g);
    setMode('new');
    setForm({
      dealer_id: '',
      company_name: g.installer_company === '(Uten firmanavn)' ? '' : g.installer_company,
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      username: '',
      password: '',
      pin_code: '',
    });
    setMsg('');
  };

  const submit = async () => {
    setBusy(true);
    setMsg('');
    try {
      const body = { installer_company: promoteModal.installer_company, mode };
      if (mode === 'existing') {
        body.dealer_id = form.dealer_id;
      } else {
        Object.assign(body, {
          company_name: form.company_name,
          contact_person: form.contact_person,
          email: form.email,
          phone: form.phone,
          address: form.address,
          username: form.username,
          password: form.password,
          pin_code: form.pin_code,
        });
      }
      const res = await fetch(`${API}/admin/demo-registrations/promote`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setMsg(`✓ ${data.moved_count} garanti(er) flyttet til ${data.target_dealer_name}!`);
        setTimeout(() => {
          setPromoteModal(null);
          load();
          if (loadDealers) loadDealers();
        }, 1200);
      } else {
        const err = await res.json();
        setMsg(err.detail || 'Kunne ikke fullføre promotering.');
      }
    } catch (e) { setMsg('Kunne ikke koble til serveren: ' + e.message); }
    setBusy(false);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return iso; }
  };

  if (loading) return <div className="text-stone-500">Laster demo-registreringer...</div>;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-stone-900">Demo-forhandlere</h2>
        <p className="text-sm text-stone-500 mt-1">
          Potensielle forhandlere som har registrert garantier via demo-kontoen. Grupperer på &ldquo;Utfører&rdquo;-feltet de skrev inn.
        </p>
      </div>

      {groups.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-12 text-center">
          <Users className="mx-auto text-stone-300 mb-4" size={48} />
          <p className="text-stone-500">Ingen demo-registreringer ennå.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="demo-groups-grid">
          {groups.map((g) => (
            <div key={g.installer_company} className="bg-white rounded-xl shadow-sm border border-stone-200 p-5" data-testid={`demo-group-${g.installer_company}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-stone-900">{g.installer_company}</h3>
                  <p className="text-xs text-stone-500 mt-0.5">Siste registrering: {formatDate(g.last_registered_at)}</p>
                </div>
                <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-1 rounded-full">DEMO</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div>
                  <div className="text-2xl font-bold text-emerald-800">{g.count}</div>
                  <div className="text-xs text-stone-500">garantier</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-stone-700">{g.unique_customers}</div>
                  <div className="text-xs text-stone-500">unike kunder</div>
                </div>
              </div>
              <div className="mb-4">
                <div className="text-xs font-medium text-stone-500 uppercase mb-1">Siste garantinumre</div>
                <div className="flex flex-wrap gap-1">
                  {g.warranties.slice(0, 5).map((w) => (
                    <span key={w.warranty_number} className="text-xs bg-stone-100 text-stone-700 px-2 py-0.5 rounded font-mono">
                      {w.warranty_number}
                    </span>
                  ))}
                  {g.warranties.length > 5 && (
                    <span className="text-xs text-stone-400 self-center">+{g.warranties.length - 5} til</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => openPromote(g)}
                className="w-full flex items-center justify-center gap-2 bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
                data-testid={`promote-btn-${g.installer_company}`}
              >
                <Sparkles size={14} /> Promoter til ekte forhandler <ArrowRight size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {promoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[92vh] overflow-y-auto" data-testid="promote-modal">
            <h3 className="text-lg font-semibold text-stone-900 mb-1">Promoter til ekte forhandler</h3>
            <p className="text-sm text-stone-500 mb-4">
              {promoteModal.count} garanti(er) fra <strong>{promoteModal.installer_company}</strong> flyttes fra demo-kontoen til den valgte forhandleren.
            </p>

            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setMode('new')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'new' ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-700'}`}
                data-testid="mode-new-btn"
              >
                Ny forhandler
              </button>
              <button
                type="button"
                onClick={() => setMode('existing')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'existing' ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-700'}`}
                data-testid="mode-existing-btn"
              >
                Eksisterende
              </button>
            </div>

            {mode === 'existing' ? (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Velg forhandler</label>
                <select
                  value={form.dealer_id}
                  onChange={(e) => setForm({ ...form, dealer_id: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  data-testid="existing-dealer-select"
                >
                  <option value="">Velg...</option>
                  {(dealers || []).filter(d => !d.is_demo_account).map((d) => (
                    <option key={d.id} value={d.id}>{d.company_name} ({d.username})</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="Firmanavn *" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="new-company-name-input" />
                  <input type="text" placeholder="Kontaktperson *" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="new-contact-input" />
                  <input type="email" placeholder="E-post *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="new-email-input" />
                  <input type="text" placeholder="Telefon *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="new-phone-input" />
                </div>
                <input type="text" placeholder="Adresse *" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="new-address-input" />
                <div className="border-t border-stone-200 pt-3">
                  <p className="text-xs font-medium text-stone-500 uppercase mb-2">Innloggingsdetaljer</p>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" placeholder="Brukernavn *" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="new-username-input" />
                    <input type="text" placeholder="Passord *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="new-password-input" />
                    <input type="text" placeholder="PIN (4 siffer) *" value={form.pin_code} onChange={(e) => setForm({ ...form, pin_code: e.target.value })} maxLength={4} className="px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="new-pin-input" />
                  </div>
                </div>
              </div>
            )}

            {msg && <p className={`text-sm mt-3 ${msg.startsWith('✓') ? 'text-emerald-600' : 'text-red-600'}`} data-testid="promote-msg">{msg}</p>}

            <div className="flex gap-3 mt-5">
              <button
                onClick={submit}
                disabled={busy || (mode === 'existing' && !form.dealer_id)}
                className="flex-1 bg-emerald-800 text-white hover:bg-emerald-900 disabled:bg-stone-300 disabled:cursor-not-allowed rounded-lg px-4 py-2 font-medium transition-colors"
                data-testid="confirm-promote-btn"
              >
                {busy ? 'Flytter...' : 'Bekreft promotering'}
              </button>
              <button
                onClick={() => setPromoteModal(null)}
                className="flex-1 bg-stone-200 text-stone-700 hover:bg-stone-300 rounded-lg px-4 py-2 font-medium transition-colors"
                data-testid="cancel-promote-btn"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
